<script lang="ts">
  import { slide } from "svelte/transition";
  import { quintOut } from "svelte/easing";

  export let data: number[];
  export let labels: string[];

  $: sum = data.reduce((sum, curr) => sum + curr, 0);

  const getPercentage = (n: number, total: number): number =>
    !n || !total ? 0 : Math.round((n / total) * 100);
  const getPercentageStyle = (n: number, total: number): string =>
    `width: ${getPercentage(n, total)}%;`;
  const getOpacity = (idx: number, dataPoints: number[]): string => {
    const fadingFactor = 1 / dataPoints.length;
    const opacity = 1 - idx * fadingFactor;
    return `opacity: ${opacity};`;
  };
  const getAnimationDelay = (idx: number, dataLength: number): string => {
    const totalDuration = 250;
    const delay = ((idx + 1) / dataLength) * totalDuration;
    return `animation-duration: ${delay}ms`;
  };
</script>

<style>
  .chart {
    z-index: 0;
    width: 100%;
  }

  @keyframes slide-up {
    from {
      transform: translateY(0.75em);
      opacity: 0;
    }
    to {
      transform: translateY(0em);
      opacity: 1;
    }
  }

  .chart-label {
    color: #4e4e4e;
    padding-right: 0.25em;
    padding-bottom: 1px;
    font-weight: 300;
    min-width: min-content;
    font-size: 0.75em;
    animation-name: slide-up;
    animation-timing-function: cubic-bezier();
  }

  .chart-labels {
    margin-left: -2px;
    display: flex;
    width: 100%;
  }

  @keyframes pop-in {
    from {
      transform: scaleY(0);
      opacity: 0;
    }
    to {
      transform: scaleY(1);
      opacity: 1;
    }
  }

  .chart-bar-animation-wrapper {
    animation-name: pop-in;
    animation-timing-function: cubic-bezier();
  }

  .chart-bar {
    background-color: var(--theme-accent);
    box-sizing: border-box;
    height: 2em;
    width: 100%;
  }

  .chart-bars {
    margin-top: 0.5em;
    display: flex;
    width: 100%;
  }

  .chart-bar-animation-wrapper:first-child > div {
    border-top-left-radius: 0.5em;
    border-bottom-left-radius: 0.5em;
  }

  .chart-bar-animation-wrapper:last-child > div {
    border-bottom-right-radius: 0.5em;
    border-top-right-radius: 0.5em;
  }

  @keyframes slide-up {
    from {
      transform: translateX(-1em);
      opacity: 0;
    }
    to {
      transform: translateX(0em);
      opacity: 1;
    }
  }

  .chart-tick {
    animation-name: slide-up;
    animation-timing-function: cubic-bezier();
    animation-duration: 200ms;
  }

  .chart-ticks {
    position: relative;
    z-index: 1;
    display: flex;
    width: 100%;
    height: 40px;
    margin-top: -40px;
  }

  .chart-tick:not(:first-child) {
    box-sizing: border-box;
    height: 100%;
    border-left: 1px solid #4e4e4e;
  }
</style>

<main>
  <div class="chart">
    <div class="chart-labels">
      {#each data as datum, idx}
        <div
          class="chart-label"
          style={`${getPercentageStyle(datum, sum)} ${getAnimationDelay(idx, data.length)}`}>
          {getPercentage(datum, sum)}%
        </div>
      {/each}
    </div>
    <div class="chart-labels">
      {#each data as datum, idx}
        <div
          class="chart-label"
          style={`${getPercentageStyle(datum, sum)} ${getAnimationDelay(idx, data.length)}`}>
          {labels[idx]}
        </div>
      {/each}
    </div>
    <div class="chart-bars">
      {#each data as datum, idx}
        <div
          class="chart-bar-animation-wrapper"
          style={`${getPercentageStyle(datum, sum)} ${getAnimationDelay(idx, data.length)}`}>
          <div
            in:slide={{ duration: 200, easing: quintOut }}
            class="chart-bar"
            style={getOpacity(idx, data)} />
        </div>
      {/each}
    </div>
    <div class="chart-ticks">
      {#each data as datum, idx}
        <div
          class="chart-tick"
          style={`${getPercentageStyle(datum, sum)} ${getAnimationDelay(idx, data.length)}`} />
      {/each}
    </div>
  </div>
</main>
